<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

import FaqTab from './tabs/FaqTab.vue';
import FilesTab from './tabs/FilesTab.vue';
import InboxesTab from './tabs/InboxesTab.vue';
import WebsiteTab from './tabs/WebsiteTab.vue';
import SettingsTab from './tabs/SettingsTab.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const knowledgeBaseId = computed(() => Number(route.params.knowledgeBaseId));
const accountId = computed(() => store.getters.getCurrentAccountId);
const isLoading = ref(false);
const activeTabIndex = ref(0);

const hasOpenAiKey = computed(() => {
  const integration = store.getters['integrations/getIntegration']('openai');
  return integration && integration.hooks && integration.hooks.length > 0;
});

const knowledgeBase = computed(() =>
  store.getters['knowledgeBases/getKnowledgeBase'](knowledgeBaseId.value)
);

const tabs = computed(() => [
  { label: t('KNOWLEDGE_BASE.SETTINGS.TABS.FAQ') },
  { label: t('KNOWLEDGE_BASE.SETTINGS.TABS.FILES') },
  { label: t('KNOWLEDGE_BASE.SETTINGS.TABS.WEBSITES') },
  { label: t('KNOWLEDGE_BASE.SETTINGS.TABS.INBOXES') },
  { label: t('KNOWLEDGE_BASE.SETTINGS.TABS.SETTINGS') },
]);

const tabKeys = ['faq', 'files', 'websites', 'inboxes', 'settings'];
const activeTabKey = computed(() => tabKeys[activeTabIndex.value]);

const handleTabChange = tab => {
  activeTabIndex.value = tabs.value.findIndex(tabItem => tabItem.label === tab.label);
};

const goBack = () => {
  router.push(`/app/accounts/${accountId.value}/settings/knowledge-bases`);
};

onMounted(async () => {
  const fetches = [store.dispatch('integrations/get', 'openai')];
  if (!knowledgeBase.value) {
    isLoading.value = true;
    fetches.push(store.dispatch('knowledgeBases/get', { accountId: accountId.value }));
  }
  try {
    await Promise.all(fetches);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="w-full p-6">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6">
      <Button
        variant="ghost"
        icon="i-lucide-arrow-left"
        size="sm"
        @click="goBack"
      />
      <div>
        <h1 class="text-2xl font-bold text-n-slate-12 m-0">
          {{ knowledgeBase?.name || $t('KNOWLEDGE_BASE.SETTINGS.LOADING') }}
        </h1>
        <p class="text-sm text-n-slate-11 m-0">
          {{ $t('KNOWLEDGE_BASE.SETTINGS.SUBTITLE') }}
        </p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <Spinner />
    </div>

    <!-- Content -->
    <div v-else-if="knowledgeBase" class="space-y-6">
      <!-- Alerta: sem chave OpenAI -->
      <div
        v-if="!hasOpenAiKey"
        class="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950 px-4 py-3"
      >
        <span class="i-lucide-triangle-alert w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
        <div>
          <p class="text-sm font-medium text-yellow-800 dark:text-yellow-200 m-0">
            {{ $t('KNOWLEDGE_BASE.ALERT.NO_OPENAI_KEY.TITLE') }}
          </p>
          <p class="text-sm text-yellow-700 dark:text-yellow-300 m-0 mt-0.5">
            {{ $t('KNOWLEDGE_BASE.ALERT.NO_OPENAI_KEY.DESCRIPTION') }}
            <router-link
              :to="`/app/accounts/${accountId}/settings/integrations/openai`"
              class="underline font-medium"
            >
              {{ $t('KNOWLEDGE_BASE.ALERT.NO_OPENAI_KEY.LINK') }}
            </router-link>
          </p>
        </div>
      </div>
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTabIndex"
        class="max-w-2xl"
        @tab-changed="handleTabChange"
      />

      <!-- Tabs e conteúdo -->
      <div class="bg-n-solid-1 border border-n-weak rounded-lg p-6">
        <FaqTab
          v-if="activeTabKey === 'faq'"
          :knowledge-base="knowledgeBase"
        />
        <FilesTab
          v-if="activeTabKey === 'files'"
          :knowledge-base="knowledgeBase"
        />
        <WebsiteTab
          v-if="activeTabKey === 'websites'"
        />
        <InboxesTab
          v-if="activeTabKey === 'inboxes'"
          :knowledge-base="knowledgeBase"
        />
        <SettingsTab
          v-if="activeTabKey === 'settings'"
          :knowledge-base="knowledgeBase"
        />
      </div>
    </div>

    <!-- Not Found -->
    <div v-else class="flex items-center justify-center py-20">
      <div class="text-center">
        <p class="text-sm text-n-slate-11">{{ $t('KNOWLEDGE_BASE.SETTINGS.NOT_FOUND') }}</p>
        <Button class="mt-4" @click="goBack">
          {{ $t('KNOWLEDGE_BASE.SETTINGS.BACK') }}
        </Button>
      </div>
    </div>
  </div>
</template>
